import { supabase } from '../../../shared/services/supabase';

export type NotificationType =
  | 'FRAMES_PENDING'
  | 'FRAMES_ORDERED_NOT_RECEIVED'
  | 'LOCATION_INACTIVE';

export type NotificationFrequency = 'DAILY' | 'WEEKLY';

export interface NotificationSettings {
  id: string;
  notification_type: NotificationType;
  enabled: boolean;
  threshold_days: number;
  frequency: NotificationFrequency;
  location_id: string | null;
  recipient_emails: string[];
  cc_regional: boolean;
  regional_email: string | null;
  created_at: string;
  updated_at: string;
}

export interface NotificationSettingsWithLocation extends NotificationSettings {
  location: {
    id: string;
    name: string;
    short_code: string;
  } | null;
}

export interface CreateNotificationSettingsInput {
  notification_type: NotificationType;
  enabled?: boolean;
  threshold_days: number;
  frequency?: NotificationFrequency;
  location_id?: string | null;
  recipient_emails: string[];
  cc_regional?: boolean;
  regional_email?: string | null;
}

export interface UpdateNotificationSettingsInput {
  id: string;
  enabled?: boolean;
  threshold_days?: number;
  frequency?: NotificationFrequency;
  recipient_emails?: string[];
  cc_regional?: boolean;
  regional_email?: string | null;
}

export const notificationSettingsService = {
  /**
   * Get all notification settings
   */
  async getNotificationSettings(): Promise<
    NotificationSettingsWithLocation[]
  > {
    const { data, error } = await supabase
      .from('notification_settings')
      .select(
        `
        *,
        location:locations (
          id,
          name,
          short_code
        )
      `
      )
      .order('notification_type', { ascending: true });

    if (error) {
      console.error('Error fetching notification settings:', error);
      throw new Error(error.message);
    }

    return data as unknown as NotificationSettingsWithLocation[];
  },

  /**
   * Get notification settings for a specific location
   */
  async getNotificationSettingsByLocation(
    location_id: string | null
  ): Promise<NotificationSettings[]> {
    const query = supabase.from('notification_settings').select('*');

    if (location_id === null) {
      query.is('location_id', null);
    } else {
      query.eq('location_id', location_id);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching notification settings:', error);
      throw new Error(error.message);
    }

    return data as NotificationSettings[];
  },

  /**
   * Get a single notification setting
   */
  async getNotificationSetting(id: string): Promise<NotificationSettings> {
    const { data, error } = await supabase
      .from('notification_settings')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching notification setting:', error);
      throw new Error(error.message);
    }

    return data as NotificationSettings;
  },

  /**
   * Create notification settings
   */
  async createNotificationSettings(
    input: CreateNotificationSettingsInput
  ): Promise<NotificationSettings> {
    const { data, error } = await supabase
      .from('notification_settings')
      // @ts-expect-error - Database types not regenerated yet
      .insert({
        notification_type: input.notification_type,
        enabled: input.enabled ?? true,
        threshold_days: input.threshold_days,
        frequency: input.frequency ?? 'DAILY',
        location_id: input.location_id ?? null,
        recipient_emails: input.recipient_emails,
        cc_regional: input.cc_regional ?? false,
        regional_email: input.regional_email ?? null,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating notification settings:', error);
      throw new Error(error.message);
    }

    return data as NotificationSettings;
  },

  /**
   * Update notification settings
   */
  async updateNotificationSettings(
    input: UpdateNotificationSettingsInput
  ): Promise<NotificationSettings> {
    const updateData: Partial<NotificationSettings> = {};

    if (input.enabled !== undefined) updateData.enabled = input.enabled;
    if (input.threshold_days !== undefined)
      updateData.threshold_days = input.threshold_days;
    if (input.frequency !== undefined) updateData.frequency = input.frequency;
    if (input.recipient_emails !== undefined)
      updateData.recipient_emails = input.recipient_emails;
    if (input.cc_regional !== undefined)
      updateData.cc_regional = input.cc_regional;
    if (input.regional_email !== undefined)
      updateData.regional_email = input.regional_email;

    const { data, error } = await supabase
      .from('notification_settings')
      // @ts-expect-error - Supabase type mismatch
      .update(updateData)
      .eq('id', input.id)
      .select()
      .single();

    if (error) {
      console.error('Error updating notification settings:', error);
      throw new Error(error.message);
    }

    return data as NotificationSettings;
  },

  /**
   * Delete notification settings
   */
  async deleteNotificationSettings(id: string): Promise<void> {
    const { error } = await supabase
      .from('notification_settings')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting notification settings:', error);
      throw new Error(error.message);
    }
  },

  /**
   * Send test notification (future implementation)
   */
  async sendTestNotification(id: string): Promise<void> {
    // This will be implemented when notifications edge function is ready
    console.log('Sending test notification for:', id);
    throw new Error('Test notification feature not yet implemented');
  },
};
